import React from 'react';
import { HOSTEL_INFO, WHATSAPP_LINK, CALL_LINK, HERO_IMAGE } from '../data/mockData';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Navigation, Phone, Mail, ArrowUpRight, ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';

const LocationPage = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <Layout>
      <PageHeader
        title="Getting to"
        titleAccent="Shoja."
        subtitle="We're tucked in the mountains — but worth every kilometre."
        bgImage={HERO_IMAGE}
      />

      <section className="pb-24 px-6 lg:px-8 bg-[#111111]">
        <div ref={ref} className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left: Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7 }}
            >
              {/* Address */}
              <div className="mb-8">
                <h4 className="text-[#D4A843] text-xs uppercase tracking-widest font-semibold mb-3">Address</h4>
                <p className="text-[#B0A898] text-sm leading-relaxed">{HOSTEL_INFO.address}</p>
              </div>

              {/* Contact */}
              <div className="mb-8">
                <h4 className="text-[#D4A843] text-xs uppercase tracking-widest font-semibold mb-3">Phone / WhatsApp</h4>
                <a href={CALL_LINK} className="text-[#B0A898] text-sm hover:text-[#F5F0E8] transition-colors duration-300 flex items-center gap-2">
                  <Phone size={14} className="text-[#D4A843]" />
                  {HOSTEL_INFO.phone}
                </a>
                <a href={`mailto:${HOSTEL_INFO.email}`} className="text-[#B0A898] text-sm hover:text-[#F5F0E8] transition-colors duration-300 flex items-center gap-2 mt-3">
                  <Mail size={14} className="text-[#D4A843]" />
                  {HOSTEL_INFO.email}
                </a>
              </div>

              {/* Directions */}
              <div className="mb-8">
                <h4 className="text-[#D4A843] text-xs uppercase tracking-widest font-semibold mb-4">How to reach</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 bg-[#1A1A1A] rounded-xl p-4 border border-[#222222]">
                    <Navigation size={16} className="text-[#D4A843] mt-0.5 shrink-0" />
                    <div>
                      <span className="text-[#F5F0E8] text-sm font-medium">From Delhi</span>
                      <p className="text-[#8A8070] text-sm mt-1">{HOSTEL_INFO.directions.fromDelhi}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-[#1A1A1A] rounded-xl p-4 border border-[#222222]">
                    <Navigation size={16} className="text-[#D4A843] mt-0.5 shrink-0" />
                    <div>
                      <span className="text-[#F5F0E8] text-sm font-medium">From Manali</span>
                      <p className="text-[#8A8070] text-sm mt-1">{HOSTEL_INFO.directions.fromManali}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-[#1A1A1A] rounded-xl p-4 border border-[#222222]">
                    <MapPin size={16} className="text-[#D4A843] mt-0.5 shrink-0" />
                    <div>
                      <span className="text-[#F5F0E8] text-sm font-medium">Nearest town</span>
                      <p className="text-[#8A8070] text-sm mt-1">{HOSTEL_INFO.directions.nearestTown}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4A843] text-[#1C1C1C] px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[#E8B940] transition-colors duration-300"
                >
                  Contact Us on WhatsApp <ArrowUpRight size={14} />
                </a>
                <a
                  href="https://maps.app.goo.gl/HTaWpiBVqh2TaKmZA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-[#3A3A3A] text-[#F5F0E8] px-8 py-3.5 rounded-full text-sm font-medium hover:border-[#D4A843] hover:text-[#D4A843] transition-colors duration-300"
                >
                  Open in Google Maps <ArrowRight size={14} />
                </a>
              </div>
            </motion.div>

            {/* Right: Map */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="rounded-2xl overflow-hidden border border-[#222222] sticky top-24"
            >
              <iframe
                src={HOSTEL_INFO.mapEmbed}
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MISFiTS Hostel Location"
                className="w-full grayscale hover:grayscale-0 transition-all duration-700"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LocationPage;
